import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { MaterialIcon } from '@lapo/ui';

/**
 * Date navigator for the journal (sits to the right of the top day's title). A calendar icon opens a
 * popover with: the five most recent journal days, a "Go to today" shortcut, and a month calendar to
 * pick any date. Any choice calls `onPick(date)` so the journal re-anchors and jumps to that day.
 */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** "Today" / "Yesterday" / weekday name for the recent-days list. */
function relativeLabel(d: Date, today: Date): string {
  const diff = Math.round((startOfDay(today).getTime() - startOfDay(d).getTime()) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long' });
}
function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function JournalDateNav({
  anchor,
  onPick,
}: {
  anchor: Date;
  onPick: (d: Date) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  // The month shown by the calendar grid (first of month). Resets to the anchor's month on open.
  const [viewMonth, setViewMonth] = useState(() => new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  // Popover is position:fixed (it lives inside the overflow-clipped sidebar), anchored to the trigger.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const today = useMemo(() => startOfDay(new Date()), []);

  const POP_WIDTH = 260;
  const toggle = (): void =>
    setOpen((o) => {
      const next = !o;
      if (next && triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        setCoords({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - POP_WIDTH - 8)) });
      }
      return next;
    });

  useEffect(() => {
    if (!open) return;
    setViewMonth(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    const onDown = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchor]);

  const recent = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(today, -i)), [today]);

  // Calendar grid: leading blanks for the weekday of the 1st, then each day of the month.
  const grid = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells: Array<Date | null> = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
    return cells;
  }, [viewMonth]);

  const pick = (d: Date): void => {
    onPick(startOfDay(d));
    setOpen(false);
  };
  const shiftMonth = (delta: number): void =>
    setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  return (
    <div className="lapo-datenav" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className="lapo-icon-btn lapo-datenav-trigger"
        title="Jump to date"
        aria-label="Jump to date"
        aria-expanded={open}
        onClick={toggle}
      >
        <MaterialIcon name="calendar_month" size={20} />
      </button>
      {open ? (
        <div
          className="lapo-datenav-pop"
          role="dialog"
          aria-label="Jump to date"
          style={coords ? { top: coords.top, left: coords.left } : undefined}
        >
          <div className="lapo-datenav-section">
            <div className="lapo-datenav-heading">Recent</div>
            {recent.map((d) => (
              <button
                key={d.toISOString()}
                type="button"
                className={`lapo-datenav-day${sameDay(d, anchor) ? ' lapo-active' : ''}`}
                onClick={() => pick(d)}
              >
                <span>{relativeLabel(d, today)}</span>
                <span className="lapo-datenav-day-date">{shortDate(d)}</span>
              </button>
            ))}
          </div>
          <button type="button" className="lapo-datenav-today" onClick={() => pick(today)}>
            <MaterialIcon name="today" size={16} />
            Go to today
          </button>
          <div className="lapo-datenav-cal">
            <div className="lapo-datenav-calhead">
              <button type="button" className="lapo-icon-btn" aria-label="Previous month" onClick={() => shiftMonth(-1)}>
                <MaterialIcon name="chevron_left" size={18} />
              </button>
              <span>{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</span>
              <button type="button" className="lapo-icon-btn" aria-label="Next month" onClick={() => shiftMonth(1)}>
                <MaterialIcon name="chevron_right" size={18} />
              </button>
            </div>
            <div className="lapo-datenav-grid">
              {WEEKDAYS.map((w) => (
                <span key={w} className="lapo-datenav-wd">{w}</span>
              ))}
              {grid.map((d, i) =>
                d ? (
                  <button
                    key={d.toISOString()}
                    type="button"
                    className={`lapo-datenav-cell${sameDay(d, anchor) ? ' lapo-active' : ''}${sameDay(d, today) ? ' lapo-today' : ''}`}
                    onClick={() => pick(d)}
                  >
                    {d.getDate()}
                  </button>
                ) : (
                  <span key={`blank-${i}`} className="lapo-datenav-cell lapo-blank" />
                ),
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
